import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core";
import { QRCode } from "react-qrcode-logo";
import mergeImages from "merge-images";
import clsx from "clsx";
import DownloadIcon from "@material-ui/icons/SystemUpdateAlt";
import { useSelector } from "react-redux";

import Styles from "../../../scss/qrCode.module.scss";
import yoelloLogo from "../../../assets/images/qrYoello.png";
import { Button, Grid, Paper } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import Loading from "../../util/Loading";
import IVenue from "../../../yoello-lib/modules/API/interfaces/IVenue";
import EColourPanel from "../../../interfaces/EColourPanel";
import DynamicForm from "../../../yoello-lib/modules/dynamicForms/DynamicForms";
import FormImageManipulator from "../../../yoello-lib/modules/components/ImageManipulator/FormImageManipulator";
import { toBase64 } from "../../../utils/toBase64";
import { resizeBase64 } from "../../../utils/resizeBase64";
import { createBase64Text } from "../../../utils/createBase64Text";
import { uploadFileToS3 } from "../../../services/s3Controller";
import { useAPIV2 } from "../../../yoello-lib/modules/API2/useAPIv2";
import APIExtensions from "../../../yoello-lib/modules/API2/APIDefs";
import { HTTPMethods } from "../../../yoello-lib/modules/API/API";
import { IReduxAppState } from "../../../reducers";
import { useSnackbarMessages } from "../../../yoello-lib/modules/components/snackbar/SnackbarContext";
import { urltoFile } from "../../../utils/urlToFile";
import { toDataUrl } from "../../../utils/toDataUrl";
import selectedImageBackground from "../../../assets/images/logo-background.png";
import useCheckPermissions from "../../../hooks/useCheckPermissions";
import { PERMISSIONS } from "../../../const/permissions.const";
import DisabledActionWrapper from "../../../yoello-lib/modules/components/DisabledActionWrapper";
import { OnboardingSource } from "../payments/types.d";
import { useAuthenticationContext } from "../../../yoello-lib/modules/auth/AuthenticationContext";

const useStyles = makeStyles((theme) => ({
  root: {
    marginBottom: "1rem",

    "& .qr-code": {
      width: "100%",
      height: "auto",
    },
  },
  title: {
    fontSize: "1.143rem",
    fontWeight: 600,
  },
}));

interface Position {
  x: number;
  y: number;
}
interface Size {
  w: number;
  h: number;
}
interface Props {
  id: string;
  venue: any;
  title: string;
  template: string;
  placeholder?: string;
  hasUploader: boolean;
  aspect?: [number, number];
  yoelloLogoSize: number;
  yoelloLogoPosition: Position;
  qrSize: number;
  qrPosition: Position;
  logoSize?: Size;
  logoPosition?: Position;
  plaqueSize: Size;
  textPosition?: Position;
  textFont?: string;
  stretch: boolean;
}

interface Image {
  src: string;
  x: number;
  y: number;
}

const QrDownloader = (props: Props) => {
  const {
    id,
    venue,
    title,
    template,
    placeholder,
    hasUploader,
    aspect,
    yoelloLogoSize,
    yoelloLogoPosition,
    qrSize,
    qrPosition,
    logoSize,
    logoPosition,
    plaqueSize,
    textPosition,
    textFont,
    stretch,
  } = props;
  const { t } = useTranslation();
  const classes = useStyles();
  const { retrieveAccessToken } = useAuthenticationContext();
  const { sendSnackbarMessage } = useSnackbarMessages()!;

  const [downloadDisabled, setDownloadDisabled] = useState<boolean>(true);
  const [submitDisabled, setSubmitDisabled] = useState<boolean>(true);

  const [baseImage, setBaseImage] = useState<{ [key: string]: Image }>({});
  const [selectedImage, setSelectedImage] = useState(placeholder);
  const [downloadImage, setDownloadImage] = useState<string>("");
  const [images, setImages] = useState<{ [key: string]: Image }>({});

  const selectedVenueId = useSelector(
    (state: IReduxAppState) => state.selectedVenueId
  );
  const Venue = useSelector(
    (state: IReduxAppState) => state.venue[selectedVenueId].venueData
  );

  const { hasPermission, onFailedInteraction } = useCheckPermissions(PERMISSIONS.VENUE_MANAGEMENT);
  const { callAPI } = useAPIV2<IVenue>(APIExtensions.venues, {
    query: selectedVenueId,
    onMountDisabled: true,
  });

  const { apiData } = useAPIV2<IVenue>(APIExtensions.venues, {
    query: selectedVenueId,
    canAPIGetCacheValue: true,
    selectorKeyParams: { venue_id: selectedVenueId },
    dispatcherParams: [selectedVenueId],
  });

  const venueUrl = `https://pay.yoello.com/v/${venue!.venue_nickname}`;

  useEffect(() => {
    const fetch = () => {
      const url =
        id === "square-logo"
          ? apiData?.url_qr_square
          : id === "large-logo"
            ? apiData?.url_qr_rectangle
            : "";

      toDataUrl(url as string).then(async (base64: any) => {
        const selectedImage = base64 as string;

        setSelectedImage(selectedImage);

        if (url === null || url === "") {
          setDownloadDisabled(true);

          arrangeImages(placeholder);
        } else {
          setDownloadDisabled(false);

          arrangeImages(selectedImage);
        }
      });
    };

    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiData, template]);

  useEffect(() => {
    const fetch = async () => {
      if (selectedImage && logoPosition && logoSize) {
        const selectedImageBackgroundResized: string = await resizeBase64(
          selectedImageBackground,
          logoSize.w,
          logoSize.h
        );

        const selectedImageResized: string = await resizeBase64(
          selectedImage,
          logoSize.w,
          logoSize.h
        );

        const images = {
          ...baseImage,
          selectedImageBackground: {
            src: selectedImageBackgroundResized,
            x: logoPosition.x,
            y: logoPosition.y,
          },
          selectedImage: {
            src: selectedImageResized,
            x: logoPosition.x,
            y: logoPosition.y,
          },
        };

        setImages(images);
      }
    };

    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedImage]);

  useEffect(() => {
    const options = {
      width: plaqueSize.w,
      height: plaqueSize.h,
    };

    mergeImages(Object.values(images), options).then((b64: string) => {
      setDownloadImage(b64);
    });

    // eslint-disable-next-line
  }, [images]);

  const arrangeImages = async (selectedImage: string | undefined) => {
    const qrCodeDestination = document.querySelector(
      `#qr-code-${id}`
    ) as HTMLImageElement;

    const qrCodeSource = document.querySelector(
      "#react-qrcode-logo"
    ) as HTMLCanvasElement;

    if (qrCodeDestination && template && qrCodeSource) {
      const qr = qrCodeSource.toDataURL("image/png");

      const qrResized = await resizeBase64(qr, qrSize, qrSize);

      const yoelloLogoResized = await resizeBase64(
        yoelloLogo,
        yoelloLogoSize,
        yoelloLogoSize
      );

      let images: { [key: string]: Image } = {
        template: { src: template, x: 0, y: 0 },
        qr: { src: qrResized, x: qrPosition.x, y: qrPosition.y },
      };

      if(Venue?.onboarding_source === OnboardingSource.YOELLO){
        images = {...images ,yoelloLogo:{
          src: yoelloLogoResized,
          x: yoelloLogoPosition.x,
          y: yoelloLogoPosition.y,
        }}
      }

      if (logoPosition !== undefined && selectedImage !== undefined) {
        const selectedImageResized = await resizeBase64(
          selectedImage,
          logoSize?.w || 0,
          logoSize?.h || 0
        );

        const selectedImageBackgroundResized = await resizeBase64(
          selectedImageBackground,
          logoSize?.w || 0,
          logoSize?.h || 0
        );

        images = {
          ...images,
          selectedImageBackground: {
            src: selectedImageBackgroundResized,
            x: logoPosition.x,
            y: logoPosition.y,
          },
          selectedImage: {
            src: selectedImageResized,
            x: logoPosition.x,
            y: logoPosition.y,
          },
        };
      }

      if (textFont && textPosition) {
        const text = createBase64Text(
          plaqueSize,
          textFont,
          venueUrl,
          textPosition
        );

        images = { ...images, text: { src: text, x: 0, y: 0 } };
      }

      setBaseImage(images);
      setImages(images);
    }
  };

  const onSelectImage = async (data: Record<string, any>) => {
    const base64 = await toBase64(data["logo"]);
    const selectedImage = base64 as string;

    setSelectedImage(selectedImage);

    if (selectedImage) {
      urltoFile(selectedImage, "image.png", "image/png").then(async function (
        file
      ) {
        try {
          const uploadImg = await uploadFileToS3(
            file,
            retrieveAccessToken,
            venue?.id! + "/qr-code/"
          );

          if (uploadImg !== undefined) {
            const key =
              id === "square-logo" ? "url_qr_square" : "url_qr_rectangle";

            await callAPI(HTTPMethods.PUT, {
              body: {
                [key]: uploadImg.url
              },
            });
          }
          sendSnackbarMessage(t("qrCode.saveImage"), "success");
        } catch (e) {
          sendSnackbarMessage(t("errors.formerrors.saveImage"), "error");
        }
      });
    }
  };

  const classnames = clsx({
    "qr-code": stretch,
  });

  return (
    <Paper className={`${Styles.QrDownloader} ${classes.root}`}>
      {venue === undefined ? (
        <Loading />
      ) : (
        <Grid
          container
          spacing={3}
          className="dialog"
          data-component-name="QR_DOWNLOADER"
        >
          {title && !hasUploader && !aspect && (
            <Grid container xs={4} alignContent="center">
              <h4 className={classes.title}>{title}</h4>
            </Grid>
          )}

          {hasUploader && aspect && (
            <Grid item xs={12} lg={4} container>
              <DynamicForm
                onHandleSubmit={onSelectImage}
                disableAllInteraction={onFailedInteraction}
                disableAll={!hasPermission}
                formData={{ logo: undefined }}
                formKeyName={
                  id === "square-logo" ? "url_qr_square" : "url_qr_rectangle"
                }
                formRows={[
                  {
                    keyName: "logo",
                    label: title,
                    hasLine: false,
                    Component: FormImageManipulator,
                    formProps: {
                      typeOfFile: "img",
                      onFailedInteraction:onFailedInteraction,
                      onChange: () => {
                        setSubmitDisabled(false);
                      },
                      onValueChange: () => {
                        setDownloadDisabled(true);

                        setSelectedImage(placeholder);
                      },
                      crop: {
                        unit: "%",
                        aspect: (aspect[0] as number) / (aspect[1] as number),
                        width: 100,
                        height: 100,
                      },
                    },
                  },
                ]}
                formButtons={{
                  rightButton: {
                    type: "submit",
                    label: t("qrCode.apply"),
                    color: EColourPanel.PRIMARY,
                    disabled: !hasPermission,
                    onClick: () => {
                      setDownloadDisabled(false);
                    },
                    style: {
                      display: submitDisabled ? "none" : "initial",
                      float: "left",
                    },
                  },
                }}
              />
            </Grid>
          )}

          <Grid
            item
            xs={12}
            lg={4}
            container
            justify="center"
            alignItems="center"
          >
            <img
              id={`qr-code-${id}`}
              alt={`qr-code-${id}`}
              className={classnames}
              src={downloadImage}
              data-component-name={`qr-code-${id}`}
            />

            <div style={{ display: "none" }}>
              <QRCode
                value={venueUrl}
                quietZone={0}
                bgColor="transparent"
                ecLevel="M"
                qrStyle="squares"
                size={200}
              />
            </div>
          </Grid>

          <Grid xs={12} lg={4} container justify="center" alignContent="center">
            <DisabledActionWrapper
              disabled={!hasPermission}
              onDisabledClick={onFailedInteraction}
            >
              <Button
                data-component-name={`qr-code-${id}-button`}
                variant="contained"
                color="primary"
                size="large"
                startIcon={<DownloadIcon />}
                onClick={() => {
                  const link = document.createElement("a");
                  link.download = `${venue!.venue_nickname!}-qr-code-${id}.png`;
                  link.href = downloadImage;
                  link.click();
                }}
                disabled={(downloadDisabled && hasUploader) || !hasPermission}
              >
                {t("forms.details.download")}
              </Button>
            </DisabledActionWrapper>

          </Grid>
        </Grid>
      )}
    </Paper>
  );
};

export default QrDownloader;
